'use client';

import React from 'react';
import { cn } from '../../utils';

/**
 * AppShell — THE persistent product-register frame: a fixed rail on the left, a
 * fixed top bar across the rest, and a single scroll-owning content column
 * inset by the rail.
 *
 * All of the geometry reads CSS vars written by tokens.css
 * (`--app-content-inset`, `--app-topnav-h`, `--app-logo-w`), which the sidebar
 * drives by stamping `data-sidebar-mode` on <html>. Nothing here — and nothing
 * in any consumer — may write a pixel width.
 *
 * It exists because `apps/internal` had reimplemented this badly: a `pl-56`
 * content column beside a `w-56` panel, no top bar at all, and an effect that
 * hand-wrote `--app-content-inset: 224px` onto the document root so that Radix
 * overlays would centre correctly. That literal contradicted the token
 * (`168px`), which is precisely the class of bug the tokens' own comment was
 * written to prevent.
 */
export function AppShell({
  sidebar,
  topBar,
  children,
  className,
}: {
  /** The rail — `AppSidebar` via an app adapter. It positions itself. */
  sidebar: React.ReactNode;
  /** Contents of the fixed top bar. Omit for a shell with no top bar. */
  topBar?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn('flex h-screen overflow-hidden bg-sidebar', className)}
      data-radix-portal-root
    >
      {sidebar}

      {topBar && (
        <div
          // App-shell chrome, so it sits on the shell layer with the sidebar.
          // The sidebar logo (z-shell-logo) still paints over it, which is the
          // intent — the logo occupies the top bar's left gutter.
          className="fixed right-0 top-0 z-shell flex-shrink-0 border-b border-border bg-card transition-[left] duration-200 ease-out"
          style={{ left: 'var(--app-content-inset, 64px)', height: 'var(--app-topnav-h)' }}
        >
          <div className="flex h-full min-w-0 w-full items-center gap-3 px-4 lg:px-6 xl:px-8">
            {topBar}
          </div>
        </div>
      )}

      <div
        className="flex-1 flex flex-col min-w-0 overflow-hidden transition-[margin-left] duration-200 ease-out"
        style={{ marginLeft: 'var(--app-content-inset, 48px)' }}
      >
        <main
          className={cn('flex-1 flex flex-col h-full min-w-0 overflow-hidden bg-background', topBar && 'pt-[var(--app-topnav-h)]')}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

export default AppShell;
